import { useState, useEffect } from "react";
import { api } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import {
  MagnifyingGlass,
  Plus,
  Trash,
  PencilSimple,
  Buildings,
  MapPin,
  MagnifyingGlassPlus,
  Globe,
  Spinner,
} from "@phosphor-icons/react";

const CURRENCIES = ["USD", "JPY", "EUR", "GBP", "INR", "AED", "SGD", "THB", "AUD", "MYR", "IDR", "MOP", "CHF", "HKD"];

const HotelLibraryPage = () => {
  const [hotels, setHotels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [showDialog, setShowDialog] = useState(false);
  const [editingHotel, setEditingHotel] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  
  // Hotel search state
  const [showSearchDialog, setShowSearchDialog] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);

  const [form, setForm] = useState({
    hotel_name: "",
    brand: "",
    city: "",
    country: "",
    address: "",
    description: "",
    amenities: "",
    default_currency: "USD",
    image_url: "",
    latitude: 0,
    longitude: 0,
  });

  useEffect(() => {
    fetchHotels();
  }, [search]);

  const fetchHotels = async () => {
    try {
      const params = search ? `?search=${encodeURIComponent(search)}` : "";
      const res = await api.get(`/hotels${params}`);
      setHotels(res.data);
    } catch (error) {
      toast.error("Failed to fetch hotels");
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (hotel = null) => {
    if (hotel) {
      setEditingHotel(hotel);
      setForm({
        hotel_name: hotel.hotel_name,
        brand: hotel.brand || "",
        city: hotel.city,
        country: hotel.country,
        address: hotel.address,
        description: hotel.description,
        amenities: hotel.amenities,
        default_currency: hotel.default_currency,
        image_url: hotel.image_url || "",
        latitude: hotel.latitude || 0,
        longitude: hotel.longitude || 0,
      });
    } else {
      setEditingHotel(null);
      setForm({
        hotel_name: "",
        brand: "",
        city: "",
        country: "",
        address: "",
        description: "",
        amenities: "",
        default_currency: "USD",
        image_url: "",
        latitude: 0,
        longitude: 0,
      });
    }
    setShowDialog(true);
  };

  const handleSave = async () => {
    if (!form.hotel_name.trim()) {
      toast.error("Please enter hotel name");
      return;
    }

    try {
      if (editingHotel) {
        await api.put(`/hotels/${editingHotel.id}`, form);
        toast.success("Hotel updated");
      } else {
        await api.post("/hotels", form);
        toast.success("Hotel added to library");
      }
      setShowDialog(false);
      fetchHotels();
    } catch (error) {
      toast.error("Failed to save hotel");
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await api.delete(`/hotels/${deleteId}`);
      toast.success("Hotel deleted");
      setDeleteId(null);
      fetchHotels();
    } catch (error) {
      toast.error("Failed to delete hotel");
    }
  };

  // Hotel Search API
  const handleSearchHotels = async () => {
    if (!searchQuery.trim()) {
      toast.error("Please enter a city or location to search");
      return;
    }

    setSearching(true);
    try {
      const res = await api.get(`/hotels/search?query=${encodeURIComponent(searchQuery)}`);
      setSearchResults(res.data);
      if (res.data.length === 0) {
        toast.info("No hotels found. Try a different location or add manually.");
      }
    } catch (error) {
      toast.error("Search failed. Please try again.");
      console.error("Search error:", error);
    } finally {
      setSearching(false);
    }
  };

  const handleSelectSearchResult = (result) => {
    setForm({
      hotel_name: result.name,
      city: result.city,
      country: result.country,
      address: result.address,
      description: result.description,
      amenities: "",
      default_currency: "USD",
      image_url: result.image_url,
      latitude: result.latitude,
      longitude: result.longitude,
    });
    setShowSearchDialog(false);
    setShowDialog(true);
    setEditingHotel(null);
  };

  return (
    <div className="p-8 max-w-6xl" data-testid="hotels-page">
      {/* Header */}
      <div className="flex items-start justify-between mb-8">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-gold mb-2">Hotel Library</p>
          <h1 className="font-serif text-4xl text-navy">Saved Properties</h1>
        </div>
        <div className="flex gap-3">
          <Button
            variant="outline"
            onClick={() => setShowSearchDialog(true)}
            className="gap-2 border-gold text-gold hover:bg-gold hover:text-white"
            data-testid="search-hotels-api-btn"
          >
            <Globe size={18} />
            Search Hotels
          </Button>
          <Button
            onClick={() => handleOpenDialog()}
            className="bg-navy hover:bg-[#2a3a5e] text-white gap-2"
            data-testid="add-hotel-btn"
          >
            <Plus size={18} />
            Add Manually
          </Button>
        </div>
      </div>

      {/* Search Local */}
      <div className="relative max-w-md mb-6">
        <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input
          placeholder="Search your saved hotels..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-10 bg-white border-border"
          data-testid="search-hotels"
        />
      </div>

      {/* Hotels Grid */}
      {loading ? (
        <div className="p-12 text-center">
          <div className="w-8 h-8 border-2 border-navy border-t-transparent animate-spin mx-auto"></div>
        </div>
      ) : hotels.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {hotels.map((hotel) => (
            <div
              key={hotel.id}
              className="bg-white border border-border overflow-hidden card-hover"
              data-testid={`hotel-card-${hotel.id}`}
            >
              {/* Hotel Image */}
              {hotel.image_url ? (
                <div className="h-40 bg-cream overflow-hidden">
                  <img 
                    src={hotel.image_url} 
                    alt={hotel.hotel_name}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                </div>
              ) : (
                <div className="h-40 bg-cream flex items-center justify-center">
                  <Buildings size={48} className="text-muted-foreground/30" />
                </div>
              )}
              
              <div className="p-4">
                <div className="flex items-start justify-between mb-2">
                  <h3 className="font-serif text-lg text-navy line-clamp-1">{hotel.hotel_name}</h3>
                  <div className="flex gap-1 ml-2 flex-shrink-0">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleOpenDialog(hotel)}
                      className="h-7 w-7 p-0"
                    >
                      <PencilSimple size={14} />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setDeleteId(hotel.id)}
                      className="h-7 w-7 p-0 text-destructive hover:text-destructive hover:bg-destructive/10"
                    >
                      <Trash size={14} />
                    </Button>
                  </div>
                </div>
                <p className="text-sm text-muted-foreground flex items-center gap-1 mb-2">
                  <MapPin size={14} />
                  {hotel.city}{hotel.country ? `, ${hotel.country}` : ""}
                </p>
                {hotel.brand && (
                  <p className="text-[10px] uppercase tracking-wider text-gold font-semibold mb-2">{hotel.brand}</p>
                )}
                {hotel.description && (
                  <p className="text-xs text-muted-foreground line-clamp-2 mb-2">{hotel.description}</p>
                )}
                {hotel.amenities && (
                  <div className="flex flex-wrap gap-1 mb-2">
                    {hotel.amenities.split(",").slice(0, 3).map((a, i) => (
                      <span key={i} className="text-[10px] bg-cream px-2 py-0.5 text-muted-foreground">
                        {a.trim()}
                      </span>
                    ))}
                  </div>
                )}
                <div className="pt-2 border-t border-border">
                  <span className="text-xs text-muted-foreground">Currency: </span>
                  <span className="text-xs font-semibold text-navy">{hotel.default_currency}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-white border border-border p-12 text-center">
          <Buildings size={48} className="mx-auto text-muted-foreground/30 mb-4" />
          <p className="text-muted-foreground mb-4">No hotels in your library</p>
          <div className="flex gap-3 justify-center">
            <Button
              variant="outline"
              onClick={() => setShowSearchDialog(true)}
              className="gap-2"
            >
              <Globe size={16} />
              Search Hotels
            </Button>
            <Button
              onClick={() => handleOpenDialog()}
              className="bg-gold hover:bg-gold/90 text-white"
            >
              Add manually
            </Button>
          </div>
        </div>
      )}

      {/* Hotel Search Dialog */}
      <Dialog open={showSearchDialog} onOpenChange={setShowSearchDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl text-navy flex items-center gap-2">
              <Globe size={24} className="text-gold" />
              Search Hotels Worldwide
            </DialogTitle>
          </DialogHeader>
          
          <div className="py-4">
            <div className="flex gap-2 mb-4">
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Enter city name (e.g., Tokyo, Paris, Dubai)"
                className="bg-cream border-border"
                onKeyPress={(e) => e.key === "Enter" && handleSearchHotels()}
                data-testid="hotel-search-input"
              />
              <Button
                onClick={handleSearchHotels}
                disabled={searching}
                className="bg-navy hover:bg-[#2a3a5e] text-white gap-2"
              >
                {searching ? <Spinner size={18} className="animate-spin" /> : <MagnifyingGlassPlus size={18} />}
                Search
              </Button>
            </div>

            <p className="text-xs text-muted-foreground mb-4">
              Powered by OpenTripMap · Results include hotels with available data
            </p>

            {/* Search Results */}
            <div className="max-h-[400px] overflow-y-auto">
              {searchResults.length > 0 ? (
                <div className="space-y-3">
                  {searchResults.map((result, idx) => (
                    <div
                      key={result.xid || idx}
                      className="flex gap-4 p-3 bg-cream hover:bg-cream-mid cursor-pointer transition-colors border border-transparent hover:border-gold"
                      onClick={() => handleSelectSearchResult(result)}
                    >
                      {result.image_url ? (
                        <img 
                          src={result.image_url} 
                          alt={result.name}
                          className="w-20 h-20 object-cover flex-shrink-0"
                          onError={(e) => {
                            e.target.src = '';
                            e.target.style.display = 'none';
                          }}
                        />
                      ) : (
                        <div className="w-20 h-20 bg-cream-mid flex items-center justify-center flex-shrink-0">
                          <Buildings size={24} className="text-muted-foreground/50" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <h4 className="font-medium text-navy line-clamp-1">{result.name}</h4>
                        <p className="text-xs text-muted-foreground flex items-center gap-1">
                          <MapPin size={12} />
                          {result.city}{result.country ? `, ${result.country}` : ""}
                        </p>
                        {result.address && (
                          <p className="text-xs text-muted-foreground line-clamp-1 mt-1">{result.address}</p>
                        )}
                        {result.description && (
                          <p className="text-xs text-muted-foreground line-clamp-2 mt-1">{result.description}</p>
                        )}
                      </div>
                      <div className="flex-shrink-0 self-center">
                        <Button size="sm" variant="outline" className="text-xs">
                          Select
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : searching ? (
                <div className="text-center py-8">
                  <Spinner size={32} className="mx-auto animate-spin text-navy mb-2" />
                  <p className="text-sm text-muted-foreground">Searching hotels...</p>
                </div>
              ) : (
                <div className="text-center py-8">
                  <MagnifyingGlassPlus size={32} className="mx-auto text-muted-foreground/30 mb-2" />
                  <p className="text-sm text-muted-foreground">Enter a city name to search for hotels</p>
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl text-navy">
              {editingHotel ? "Edit Hotel" : "Add Hotel"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
            {/* Image Preview */}
            {form.image_url && (
              <div className="mb-4">
                <img 
                  src={form.image_url} 
                  alt="Hotel preview"
                  className="w-full h-40 object-cover"
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              </div>
            )}
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Hotel Name *</Label>
                <Input
                  value={form.hotel_name}
                  onChange={(e) => setForm({ ...form, hotel_name: e.target.value })}
                  placeholder="e.g. The Hotel Seiryu Kyoto"
                  className="bg-cream border-border"
                  data-testid="hotel-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">City</Label>
                <Input
                  value={form.city}
                  onChange={(e) => setForm({ ...form, city: e.target.value })}
                  placeholder="e.g. Kyoto"
                  className="bg-cream border-border"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Country</Label>
                <Input
                  value={form.country}
                  onChange={(e) => setForm({ ...form, country: e.target.value })}
                  placeholder="e.g. Japan"
                  className="bg-cream border-border"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Brand / Chain</Label>
                <Input
                  value={form.brand || ""}
                  onChange={(e) => setForm({ ...form, brand: e.target.value })}
                  placeholder="e.g. Marriott, Taj, ITC"
                  className="bg-cream border-border"
                  data-testid="hotel-brand-input"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Default Currency</Label>
                <Select
                  value={form.default_currency}
                  onValueChange={(v) => setForm({ ...form, default_currency: v })}
                >
                  <SelectTrigger className="bg-cream border-border">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CURRENCIES.map((c) => (
                      <SelectItem key={c} value={c}>{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Full Address</Label>
              <Input
                value={form.address}
                onChange={(e) => setForm({ ...form, address: e.target.value })}
                placeholder="e.g. 2-204-2 Kiyomizu, Higashiyama-ku, Kyoto"
                className="bg-cream border-border"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Description</Label>
              <Textarea
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="Brief description — location, signature features, why recommended"
                className="bg-cream border-border"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Amenities (comma-separated)</Label>
              <Input
                value={form.amenities}
                onChange={(e) => setForm({ ...form, amenities: e.target.value })}
                placeholder="Concierge, Dining, Spa, Pool, Gym"
                className="bg-cream border-border"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Image URL (optional)</Label>
              <Input
                value={form.image_url}
                onChange={(e) => setForm({ ...form, image_url: e.target.value })}
                placeholder="https://..."
                className="bg-cream border-border"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave} className="bg-navy hover:bg-[#2a3a5e] text-white" data-testid="save-hotel-btn">
              {editingHotel ? "Update" : "Add Hotel"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Hotel</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to remove this hotel from your library? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default HotelLibraryPage;
